import React, { useState } from "react";
import { DashboardLayout } from "@/components/dashboard/dashboard-layout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { format, startOfWeek, addDays, eachDayOfInterval, endOfWeek } from "date-fns";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { useQuery } from "convex/react";
import { api } from "../../../../../../convex/_generated/api";
import { AddEventSheet } from "./AddEventSheet";
import EventDetailsSheet from "../../../components/calendar/EventDetailsSheet";
import { EditEventSheet } from "../../../components/calendar/EditEventSheet";

export default function TeamCalendarPage() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [isAddEventSheetOpen, setIsAddEventSheetOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<any>(null);
  const [isEventDetailsOpen, setIsEventDetailsOpen] = useState(false);
  const [isEditEventOpen, setIsEditEventOpen] = useState(false);
  const [eventToEdit, setEventToEdit] = useState<any>(null);

  // Get team members and events from real data
  const teamMembers = useQuery(api.calendar.getOrgMembers) || [];

  // Get events for the current week
  const startDate = startOfWeek(currentDate);
  const endDate = endOfWeek(currentDate);

  const events =
    useQuery(api.calendar.getEventsByDateRange, {
      startDate: format(startDate, "yyyy-MM-dd"),
      endDate: format(endDate, "yyyy-MM-dd"),
    }) || [];

  const weekDays = eachDayOfInterval({ start: startDate, end: endDate });

  const handlePrevWeek = () => {
    setCurrentDate(addDays(currentDate, -7));
  };

  const handleNextWeek = () => {
    setCurrentDate(addDays(currentDate, 7));
  };

  const getEventsForDayAndMember = (day: Date, memberId: string) => {
    return events.filter((event: any) => {
      // Check if the event is assigned to this member
      const isAssignedToMember = event.assignedStaff.some((staffId: any) => staffId === memberId);

      // Check if the event is on this day
      // Parse the date string as local date to avoid timezone issues
      const [year, month, dayOfMonth] = event.startDate.split("-").map(Number);
      const eventDate = new Date(year, month - 1, dayOfMonth); // month is 0-indexed

      const isSameDay =
        eventDate.getDate() === day.getDate() &&
        eventDate.getMonth() === day.getMonth() &&
        eventDate.getFullYear() === day.getFullYear();

      return isAssignedToMember && isSameDay;
    });
  };

  // Color palette for team members
  const colors = [
    "bg-blue-500",
    "bg-green-500",
    "bg-purple-500",
    "bg-yellow-500",
    "bg-red-500",
    "bg-indigo-500",
    "bg-pink-500",
    "bg-teal-500",
    "bg-orange-500",
  ];

  const getColorForMember = (index: number) => {
    return colors[index % colors.length];
  };

  // Handle event click
  const handleEventClick = (event: any) => {
    // Transform the event to match the expected format for EventDetailsSheet
    const transformedEvent = {
      id: event._id,
      _id: event._id,
      title: event.title,
      date: new Date(), // This will be set properly below
      time: `${event.startTime} - ${event.endTime}`,
      description: event.description || "",
      eventType: event.eventType,
      serviceLocation: event.serviceLocationId || "",
      location: event.location,
      address: event.address,
      assignedStaff: event.assignedStaff,
      businessContext: event.businessContext,
      // Include additional fields needed for editing
      startDate: event.startDate,
      endDate: event.endDate,
      startTime: event.startTime,
      endTime: event.endTime,
      isAllDay: event.isAllDay,
      businessId: event.businessId,
      serviceLocationId: event.serviceLocationId,
      createdByName: event.createdByName,
    };

    // Parse the date properly
    const [year, month, dayOfMonth] = event.startDate.split("-").map(Number);
    transformedEvent.date = new Date(year, month - 1, dayOfMonth);

    setSelectedEvent(transformedEvent);
    setIsEventDetailsOpen(true);
  };

  // Handle edit event
  const handleEditEvent = (event: any) => {
    setEventToEdit(event);
    setIsEditEventOpen(true);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold tracking-tight">Team Calendar</h1>
          <Button onClick={() => setIsAddEventSheetOpen(true)}>
            <Plus className="mr-2 h-4 w-4" /> Add Event
          </Button>
        </div>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between pb-6">
              <div className="flex items-center space-x-4">
                <Button variant="outline" size="icon" onClick={handlePrevWeek}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <h2 className="text-xl font-semibold">
                  {format(startDate, "MMMM d")} - {format(endDate, "MMMM d, yyyy")}
                </h2>
                <Button variant="outline" size="icon" onClick={handleNextWeek}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <th className="w-40 border bg-gray-50 p-2">Team Member</th>
                    {weekDays.map((day, index) => (
                      <th key={index} className="border bg-gray-50 p-2">
                        <div className="text-center">
                          <div>{format(day, "EEE")}</div>
                          <div>{format(day, "MMM d")}</div>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {teamMembers.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="border p-4 text-center text-gray-500">
                        No team members found
                      </td>
                    </tr>
                  ) : (
                    teamMembers.map((member: any, memberIndex: any) => (
                      <tr key={member._id}>
                        <td className="border p-2">
                          <div>
                            <div className="font-medium">{member.name}</div>
                            <div className="text-sm text-gray-500">{member.email}</div>
                          </div>
                        </td>
                        {weekDays.map((day, dayIndex) => {
                          const memberEvents = getEventsForDayAndMember(day, member._id);
                          return (
                            <td key={dayIndex} className="h-24 border p-2 align-top">
                              <div className="space-y-1">
                                {memberEvents.map((event: any) => (
                                  <div
                                    key={event._id}
                                    className={`${getColorForMember(memberIndex)} cursor-pointer rounded p-1 text-xs text-white transition-opacity hover:opacity-80`}
                                    onClick={() => handleEventClick(event)}
                                  >
                                    <div className="font-medium">{event.title}</div>
                                    <div>
                                      {event.startTime} - {event.endTime}
                                    </div>
                                    <div className="truncate">
                                      {event.location || event.address || "No location"}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      <AddEventSheet
        isOpen={isAddEventSheetOpen}
        onClose={() => setIsAddEventSheetOpen(false)}
        onEventAdded={() => {
          // Optionally refresh data or show success message
          setIsAddEventSheetOpen(false);
        }}
        defaultDate={format(currentDate, "yyyy-MM-dd")}
      />
      <EventDetailsSheet
        open={isEventDetailsOpen}
        onOpenChange={setIsEventDetailsOpen}
        event={selectedEvent}
        onEditEvent={handleEditEvent}
      />
      <EditEventSheet
        isOpen={isEditEventOpen}
        onClose={() => {
          setIsEditEventOpen(false);
          setEventToEdit(null);
        }}
        event={eventToEdit}
        onEventUpdated={() => {
          setIsEditEventOpen(false);
          setEventToEdit(null);
          // Events will automatically refresh due to the useQuery
        }}
      />
    </DashboardLayout>
  );
}
